import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaEye, FaHeart } from 'react-icons/fa';
import { MdOutlineMenuBook } from 'react-icons/md';
import api from '../services/api';
import '../styles/SearchResults.css';
import getBookCoverUrl from '../utils/getBookCoverUrl';
import { getProfileImageUrl } from '../utils/getProfileImageUrl';

function SearchResults() {
  const location = useLocation();
  const query = new URLSearchParams(location.search).get('query');
  const [activeTab, setActiveTab] = useState('kitaplar');
  const [booksByTitle, setBooksByTitle] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [following, setFollowing] = useState({});
  const navigate = useNavigate();
  const currentUserId = localStorage.getItem('userId');
  const token = localStorage.getItem('token');

  useEffect(() => {
    const fetchSearchResults = async () => {
      try {
        const response = await api.get(`/search/search?query=${encodeURIComponent(query)}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        // Profile sayfasındaki doğru mapping yapısını kullan
        const mappedBooks = (response.data.booksByTitle || []).map(book => ({
          ...book,
          id: book.book_id,
          reads: book.total_views,
          likes: book.total_likes,
          publishedChapters: book.published_chapters_count || book.chapters
        }));
        
        setBooksByTitle(mappedBooks);
        setAuthors(response.data.authors || []);
        fetchFollowStatus(response.data.authors || []);
      } catch (error) {
        console.error('Error fetching search results:', error);
      }
    };

    const fetchFollowStatus = async (authors) => {
      try {
        const followStatus = {};
        for (const author of authors) {
          const followResponse = await api.get(`/search/isFollowing/${currentUserId}/${author.user_id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          followStatus[author.user_id] = followResponse.data.isFollowing;
        }
        setFollowing(followStatus);
      } catch (error) {
        console.error('Error fetching follow status:', error);
      }
    };

    if (query) {
      fetchSearchResults();
    }
  }, [query, currentUserId, token]);

  const handleBookClick = (bookId) => {
    navigate(`/book/${bookId}`);
  };

  const handleUserClick = (userId) => {
    navigate(`/profile/${userId}`);
  };

  const toggleFollow = async (userId, event) => {
    // Prevent clicking the button from navigating to the profile
    event.stopPropagation();
    
    try {
      const endpoint = following[userId] ? `/search/unfollow/${currentUserId}/${userId}` : `/search/follow/${currentUserId}/${userId}`;
      await api.post(endpoint, null, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setFollowing(prev => ({ ...prev, [userId]: !prev[userId] }));
    } catch (error) {
      console.error('Error while toggling follow:', error);
    }
  };

  return (
    <div className="search-container">
      <div className="search-header">
        <h1 className="search-title">
          Arama Sonuçları: <span className="search-query">"{query}"</span>
        </h1>
        
        <div className="search-tabs">
          <button
            className={`search-tab ${activeTab === 'kitaplar' ? 'search-tab-active' : ''}`}
            onClick={() => setActiveTab('kitaplar')}
          >
            📚 Kitaplar
          </button>
          <button
            className={`search-tab ${activeTab === 'kullanicilar' ? 'search-tab-active' : ''}`}
            onClick={() => setActiveTab('kullanicilar')}
          >
            👥 Kullanıcılar
          </button>
        </div>
      </div>
      
      <div className="search-content">
        {activeTab === 'kitaplar' && (
          <div className="search-books-section">
            {booksByTitle.length > 0 ? (
              <div className="search-books-grid">
                {booksByTitle.map((book) => (
                  <div
                    key={book.book_id}
                    className="search-book-card"
                    onClick={() => handleBookClick(book.book_id)}
                  >
                    <div className="search-book-cover-container">
                      <img 
                        src={getBookCoverUrl(book.cover_image_url)} 
                        alt={book.title} 
                        className="search-book-cover" 
                      />
                    </div>
                    <div className="search-book-info">
                      <h3 className="search-book-title">{book.title}</h3>
                      <p className="search-book-author">{book.author}</p>
                      <div className="search-book-stats">
                        <span className="search-stat">
                          <FaEye className="search-stat-icon" />
                          {book.reads}
                        </span>
                        <span className="search-stat">
                          <FaHeart className="search-stat-icon" />
                          {book.likes}
                        </span>
                        <span className="search-stat">
                          <MdOutlineMenuBook className="search-stat-icon" />
                          {book.publishedChapters}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="search-empty-state">
                <div className="search-empty-icon">📚</div>
                <p className="search-empty-message">Kitap bulunamadı.</p>
                <p className="search-empty-subtitle">Farklı anahtar kelimeler deneyebilirsiniz.</p>
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'kullanicilar' && (
          <div className="search-authors-section">
            {authors.length > 0 ? (
              <div className="search-authors-list">
                {authors.map((author) => (
                  <div 
                    key={author.user_id} 
                    className="search-author-card"
                    onClick={() => handleUserClick(author.user_id)}
                  >
                    <div className="search-author-info">
                      <img
                        src={getProfileImageUrl(author.profile_image_url)}
                        alt={author.author}
                        className="search-author-avatar"
                      />
                      <div className="search-author-details">
                        <h3 className="search-author-name">{author.author}</h3>
                        <p className="search-author-stats">{author.total_books} kitap</p>
                      </div>
                    </div>
                    <button
                      className={`search-follow-btn ${following[author.user_id] ? 'search-following' : ''}`}
                      onClick={(e) => toggleFollow(author.user_id, e)}
                    >
                      {following[author.user_id] ? 'Takip Ediliyor' : 'Takip Et'}
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="search-empty-state">
                <div className="search-empty-icon">👥</div>
                <p className="search-empty-message">Kullanıcı bulunamadı.</p>
                <p className="search-empty-subtitle">Farklı anahtar kelimeler deneyebilirsiniz.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default SearchResults;
